import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Separator } from '@/shared/components/ui/separator';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { useActiveOrganization } from '@/features/organizations/public';
import { useTenantWorkSchedule } from '../hooks/useTenantWorkSchedule';
import { useUpsertTenantWorkSchedule } from '../hooks/useUpsertTenantWorkSchedule';
import {
  createTenantWorkScheduleSchema,
  type TenantWorkScheduleFormInput,
  type TenantWorkScheduleFormValues,
} from '../schemas';
import { WorkScheduleSectionSkeleton } from './WorkScheduleSectionSkeleton';
import { WorkScheduleOverridesList } from './WorkScheduleOverridesList';
import { WorkScheduleSegmentsField } from './WorkScheduleSegmentsField';
import type { WorkSegmentDto } from '../types';

const EMPTY_SEGMENTS = [{ startTime: '', endTime: '' }];

/** Sorts by `order` and drops it — the form never edits `order` directly. */
function toFormSegments(segments: WorkSegmentDto[] | null | undefined) {
  if (!segments || segments.length === 0) return EMPTY_SEGMENTS;
  return [...segments]
    .sort((a, b) => a.order - b.order)
    .map((s) => ({ startTime: s.startTime ?? '', endTime: s.endTime ?? '' }));
}

/**
 * Organization settings section — tenant work schedule form plus the list of
 * per-member overrides. Restricted to owner/admin (the nav item is also hidden
 * for other roles; the backend enforces 403 independently).
 */
export function WorkScheduleSection() {
  const { t } = useTranslation('workSchedules');
  const { activeOrganization } = useActiveOrganization();
  const canManage = activeOrganization?.role === 'owner' || activeOrganization?.role === 'admin';

  const { data, isLoading, isError, refetch } = useTenantWorkSchedule();
  const upsert = useUpsertTenantWorkSchedule();

  // Three generics (Input, Context, Output): the schema coerces strings → numbers,
  // so the form's input type (string fields from FormFieldInput) differs from the
  // validated output the submit handler receives. Collapsing them breaks the resolver type.
  const { control, handleSubmit, reset, formState } = useForm<
    TenantWorkScheduleFormInput,
    unknown,
    TenantWorkScheduleFormValues
  >({
    resolver: zodResolver(createTenantWorkScheduleSchema(t)),
    defaultValues: { segments: EMPTY_SEGMENTS, workingDays: '' },
  });

  useEffect(() => {
    if (!data) return;
    reset({ segments: toFormSegments(data.segments), workingDays: data.workingDays });
  }, [data, reset]);

  if (!canManage) {
    return (
      <div data-testid="work-schedules-forbidden" className="py-4 text-sm text-muted-foreground">
        {t('tenant.loadError')}
      </div>
    );
  }

  if (isLoading) return <WorkScheduleSectionSkeleton />;

  if (isError) {
    return (
      <div
        data-testid="work-schedules-tenant-error"
        className="rounded-lg border border-destructive/30 bg-destructive/5 p-4"
      >
        <p className="text-sm text-destructive">{t('tenant.loadError')}</p>
        <Button
          variant="outline"
          size="sm"
          data-testid="work-schedules-retry-btn"
          className="mt-3"
          onClick={() => refetch()}
        >
          {t('member.cancel')}
        </Button>
      </div>
    );
  }

  const onSubmit = async (values: TenantWorkScheduleFormValues) => {
    try {
      await upsert.mutateAsync({
        segments: values.segments.map((s, order) => ({ order, ...s })),
        workingDays: values.workingDays,
      });
      reset(values);
    } catch {
      // global handler shows error toast
    }
  };

  return (
    <div className="space-y-8" data-testid="work-schedules-tenant-section">
      <div>
        <div className="flex items-center gap-2 pb-1">
          <h2 className="text-base font-semibold md:text-lg">{t('tenant.title')}</h2>
          {data?.isDefault && (
            <Badge variant="secondary" data-testid="work-schedules-tenant-default-badge">
              {t('tenant.isDefaultBadge')}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{t('tenant.description')}</p>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
          <FormFieldInput
            control={control}
            name="workingDays"
            type="number"
            label={t('tenant.workingDays')}
            floatingLabel
            data-testid="work-schedules-tenant-days-input"
          />

          <WorkScheduleSegmentsField control={control} />

          {data && (
            <p
              className="text-xs text-muted-foreground"
              data-testid="work-schedules-tenant-derived-hours"
            >
              {t('overrides.hoursPerDay', { count: data.hoursPerDay })}
            </p>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!formState.isDirty || upsert.isPending}
              data-testid="work-schedules-tenant-save-button"
            >
              {upsert.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {upsert.isPending ? t('tenant.saving') : t('tenant.save')}
            </Button>
          </div>
        </form>
      </div>

      <Separator />

      <WorkScheduleOverridesList />
    </div>
  );
}
